import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { SplitText, useReveal } from 'components';
import { gsap, useGSAP } from 'src/utils/gsap';
import { DESKTOP_QUERY, prefersReducedMotion } from 'src/utils/motion';

import { usePage } from '../../pages';
import { SceneAnalytics, SceneOrders, ScenePrices, SceneStock } from './scenes';

import style from './HowItWorks.module.scss';

const SCENES = [
  { Content: ScenePrices, texts: 'prices', className: style.sPrices },
  { Content: SceneOrders, texts: 'orders', className: style.sOrders },
  { Content: SceneStock, texts: 'stock', className: style.sStock },
  { Content: SceneAnalytics, texts: 'analytics', className: style.sAnalytics }
];

const TICKS = Array.from({ length: 72 }, (_, k) => ({ k, big: k % 18 === 0, mid: k % 6 === 0 }));

// play remounts the content so its CSS animations start over
const Scene = ({ index, state, play, how }) => {
  const { Content, texts, className } = SCENES[index];

  return (
    <div className={cn(style.scene, className, state && style[state])}>
      <Content
        key={play}
        c={how[texts]}
        active={Boolean(state)}
      />
    </div>
  );
};

// Mobile: every block has its own window that plays when scrolled into view
const Lens = ({ index, how }) => {
  const ref = useRef(null);
  const [active, setActive] = useState(false);
  const [play, setPlay] = useState(0);

  useEffect(() => {
    // Observe the window, not the scene: a closed iris (clip-path 0%) never intersects
    const observer = new IntersectionObserver(
      ([entry]) => {
        setActive(entry.isIntersecting);
        if (entry.isIntersecting) setPlay((p) => p + 1);
      },
      { threshold: 0.5 }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={style.lens}
      aria-hidden='true'
    >
      <div className={style.porthole}>
        <Scene
          index={index}
          state={active ? 'on' : ''}
          play={play}
          how={how}
        />
      </div>
    </div>
  );
};

const HowItWorks = () => {
  const { t } = useTranslation('page');
  const { id } = usePage();
  const how = t(`${id}.how`, { returnObjects: true });

  const sectionRef = useRef(null);
  const blockRefs = useRef([]);
  const dialRef = useRef(null);
  const progressRef = useRef(null);
  const currentRef = useRef(-1);
  const outTimers = useRef([]);

  const [current, setCurrent] = useState(-1);
  const [outgoing, setOutgoing] = useState([]);
  const [plays, setPlays] = useState([0, 0, 0, 0]);
  const [iris, setIris] = useState(0);

  useReveal(sectionRef);

  const showScene = (i) => {
    const prev = currentRef.current;
    if (i === prev) return;

    currentRef.current = i;
    setCurrent(i);
    setPlays((list) => list.map((n, j) => (j === i ? n + 1 : n)));
    setIris((n) => n + 1);
    setOutgoing((list) => [...list.filter((j) => j !== i && j !== prev), ...(prev >= 0 ? [prev] : [])]);
    if (prev >= 0) {
      clearTimeout(outTimers.current[prev]);
      outTimers.current[prev] = setTimeout(() => setOutgoing((list) => list.filter((j) => j !== prev)), 1500);
    }
  };

  // Desktop: the block crossing the middle of the screen sets the scene, the dial follows
  useGSAP(
    () => {
      const desktop = window.matchMedia(DESKTOP_QUERY);
      let rotate = (deg) => dialRef.current.setAttribute('transform', `rotate(${deg} 100 100)`);
      if (!prefersReducedMotion) {
        gsap.set(dialRef.current, { svgOrigin: '100 100' });
        rotate = gsap.quickTo(dialRef.current, 'rotation', { duration: 0.9, ease: 'power3.out' });
      }

      const update = () => {
        if (!desktop.matches) return;

        const blocks = blockRefs.current;
        const n = blocks.length;
        const center = window.innerHeight / 2;
        const first = blocks[0].getBoundingClientRect();
        const last = blocks[n - 1].getBoundingClientRect();
        let x = 0;

        if (center >= last.bottom) x = n - 0.001;
        else if (center > first.top) {
          blocks.forEach((block, i) => {
            const rect = block.getBoundingClientRect();
            if (center >= rect.top && center < rect.bottom) x = i + (center - rect.top) / rect.height;
          });
        }

        showScene(Math.min(n - 1, Math.floor(x)));
        progressRef.current.style.strokeDashoffset = (100 - (x / n) * 100).toFixed(2);
        rotate(-Math.max(0, Math.min(n - 1, x - 0.5)) * 90);
      };

      showScene(0);
      update();
      window.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);

      return () => {
        window.removeEventListener('scroll', update);
        window.removeEventListener('resize', update);
        outTimers.current.forEach(clearTimeout);
      };
    },
    { scope: sectionRef }
  );

  return (
    <section
      id='jak'
      ref={sectionRef}
      className='sec tone-light'
      data-tone='light'
    >
      <div className='wrap'>
        <div className={style.head}>
          <p className='mono-label'>{how.label}</p>
          <SplitText
            as='h2'
            className='h2'
            reveal
          >
            {how.title}
          </SplitText>
        </div>
        <div className={style.grid}>
          <div
            className={style.stage}
            aria-hidden='true'
          >
            <div className={style.stageIn}>
              <svg
                className={style.dial}
                viewBox='0 0 200 200'
              >
                <circle
                  className={style.trk}
                  cx='100'
                  cy='100'
                  r='92'
                />
                <circle
                  ref={progressRef}
                  className={style.prog}
                  cx='100'
                  cy='100'
                  r='92'
                  pathLength='100'
                />
                <g ref={dialRef}>
                  {TICKS.map(({ k, big, mid }) => (
                    <line
                      key={k}
                      className={cn(style.tick, big && style.big)}
                      x1='100'
                      y1={big ? 12 : mid ? 14.5 : 16}
                      x2='100'
                      y2='19'
                      transform={`rotate(${k * 5} 100 100)`}
                    />
                  ))}
                  {how.blocks.map((block, i) => (
                    <text
                      key={i}
                      className={cn(style.lbl, i === current && style.on)}
                      x='100'
                      y='4.2'
                      textAnchor='middle'
                      transform={`rotate(${i * 90} 100 100)`}
                    >
                      {block.label}
                    </text>
                  ))}
                </g>
                <path
                  className={style.pointer}
                  d='M97.4 5.6h5.2L100 9.6z'
                />
              </svg>
              <div className={style.porthole}>
                {SCENES.map((_, i) => (
                  <Scene
                    key={i}
                    index={i}
                    state={i === current ? 'on' : outgoing.includes(i) ? 'out' : ''}
                    play={plays[i]}
                    how={how}
                  />
                ))}
              </div>
              <div
                key={iris}
                className={cn(style.iris, iris > 0 && style.go)}
              />
            </div>
          </div>

          <div className={style.blocks}>
            {how.blocks.map((block, i) => (
              <article
                key={i}
                ref={(el) => (blockRefs.current[i] = el)}
                className={cn(style.block, i === current && style.on)}
              >
                <Lens
                  index={i}
                  how={how}
                />
                <p className={style.idx}>
                  <b>{String(i + 1).padStart(2, '0')}</b> / 04 · {block.label}
                </p>
                <h3>{block.title}</h3>
                <p>{block.text}</p>
                <ul
                  className={style.kw}
                  data-reveal
                >
                  {block.keywords.map((keyword) => (
                    <li key={keyword}>{keyword}</li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
